import { SILENT_ERROR } from '@/core/interceptors/error.interceptor';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, EMPTY, filter, switchMap, take, timer } from 'rxjs';
import { APP_VERSION } from 'src/environments/app-version';
import { environment } from 'src/environments/environment';

export interface AppVersionInfo {
    buildId: string;
    version?: string;
    builtAt?: string;
}

const RELOAD_ATTEMPT_KEY = 'betta-version-reload-attempt';
/** Query param used only to bust document cache on Refresh Now; stripped after load. */
const RELOAD_QUERY_PARAM = '_ba';

@Injectable({
    providedIn: 'root'
})
export class VersionCheckService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);

    private readonly updateAvailable = signal(false);
    readonly isUpdateAvailable = this.updateAvailable.asReadonly();

    private readonly refreshing = signal(false);
    readonly isRefreshing = this.refreshing.asReadonly();

    private started = false;
    private pendingRemoteBuildId: string | null = null;

    /** Local build baked into this JS bundle (stamped at deploy). */
    readonly localBuildId = APP_VERSION.buildId;

    start(): void {
        const config = environment.versionCheck;
        if (this.started || !config?.enabled) {
            return;
        }
        this.started = true;
        this.clearStaleReloadGuard();
        this.scheduleStripReloadQueryParam();

        // Clear any older session dismiss keys from previous builds
        try {
            sessionStorage.removeItem('betta-version-dismissed-build');
        } catch {
            // ignore
        }

        timer(0, config.pollIntervalMs)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => this.fetchRemote(config.url))
            )
            .subscribe((remote) => this.applyRemote(remote));
    }

    /**
     * Loads the open route as a new document URL so a previously cached page
     * is not reused. The route response is `no-store`, and hashed script names
     * in that document pull the new build. Does not clear auth or storage.
     * Do not prefetch the document first: that fetch can store a stale page
     * which the following navigation then reuses.
     */
    refreshNow(): void {
        if (this.refreshing()) {
            return;
        }
        this.refreshing.set(true);

        if (this.pendingRemoteBuildId) {
            try {
                sessionStorage.setItem(
                    RELOAD_ATTEMPT_KEY,
                    JSON.stringify({
                        buildId: this.pendingRemoteBuildId,
                        at: Date.now()
                    })
                );
            } catch {
                // sessionStorage may be unavailable; still reload
            }
        }

        const url = new URL(window.location.href);
        url.searchParams.set(RELOAD_QUERY_PARAM, Date.now().toString());
        window.location.replace(url.toString());
    }

    /**
     * Hides the banner for now. Polling continues; the next poll re-shows it
     * if the tab is still on an older build than version.json.
     */
    dismiss(): void {
        this.updateAvailable.set(false);
    }

    private fetchRemote(url: string) {
        const cacheBusted = `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
        return this.http
            .get<AppVersionInfo>(cacheBusted, {
                context: new HttpContext().set(SILENT_ERROR, true),
                headers: new HttpHeaders({
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache'
                })
            })
            .pipe(catchError(() => EMPTY));
    }

    private applyRemote(remote: AppVersionInfo): void {
        const remoteBuildId = remote?.buildId?.trim();
        if (!remoteBuildId || remoteBuildId === this.localBuildId) {
            this.pendingRemoteBuildId = null;
            this.updateAvailable.set(false);
            this.clearReloadAttemptIfMatched(remoteBuildId);
            return;
        }

        this.pendingRemoteBuildId = remoteBuildId;
        this.updateAvailable.set(true);
    }

    private clearReloadAttemptIfMatched(remoteBuildId: string | undefined): void {
        if (!remoteBuildId) {
            return;
        }
        try {
            const attemptRaw = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
            if (attemptRaw) {
                const attempt = JSON.parse(attemptRaw) as { buildId?: string };
                if (attempt?.buildId === remoteBuildId || remoteBuildId === this.localBuildId) {
                    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
                }
            }
        } catch {
            // ignore
        }
    }

    private clearStaleReloadGuard(): void {
        try {
            const attemptRaw = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
            if (!attemptRaw) {
                return;
            }
            const attempt = JSON.parse(attemptRaw) as { buildId?: string; at?: number };
            if (!attempt?.at || Date.now() - attempt.at > 2 * 60 * 1000) {
                sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
            }
        } catch {
            try {
                sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
            } catch {
                // ignore
            }
        }
    }

    /**
     * `_ba` is only for cache-busting Refresh Now. Strip it after the router
     * settles so it does not stay in the address bar.
     */
    private scheduleStripReloadQueryParam(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => this.stripReloadQueryParam());

        Promise.resolve().then(() => this.stripReloadQueryParam());
    }

    private stripReloadQueryParam(): void {
        try {
            const tree = this.router.parseUrl(this.router.url);
            if (!(RELOAD_QUERY_PARAM in tree.queryParams)) {
                return;
            }
            void this.router.navigate([], {
                queryParams: { [RELOAD_QUERY_PARAM]: null },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        } catch {
            // ignore
        }
    }
}
